import { gql, TypedDocumentNode } from "@apollo/client";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";
import type { UniversityProbation } from "./types";
import type { UniversityProbationType } from "./_constants";

export type GetUniversityProbationsQuery = {
  university: {
    id: string;
    name: string;
    probations: {
      totalCount: number;
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor?: string;
        endCursor?: string;
      };
      nodes: UniversityProbation[];
    };
  } | null;
};

export type GetUniversityProbationsQueryVariables = {
  universityId: string;
  first?: number;
  last?: number;
  before?: string;
  after?: string;
  activeOnly?: boolean;
  personId?: string;
  search?: string;
  status?: "ACTIVE" | "INACTIVE";
  orderBy?: {
    field: "ACTIVE";
    direction: "ASC" | "DESC";
  };
};

export const GET_UNIVERSITY_PROBATIONS_QUERY: TypedDocumentNode<
  GetUniversityProbationsQuery,
  GetUniversityProbationsQueryVariables
> = gql`
  query GetUniversityProbations(
    $universityId: ID!
    $first: Int
    $last: Int
    $before: String
    $after: String
    $activeOnly: Boolean
    $personId: ID
    $search: String
    $status: BackofficeEducationUniversityProbationStatusEnum
    $orderBy: BackofficeEducationUniversityProbationOrderByInput
  ) {
    university(id: $universityId) {
      id
      name
      probations(
        first: $first
        last: $last
        before: $before
        after: $after
        activeOnly: $activeOnly
        personId: $personId
        search: $search
        status: $status
        orderBy: $orderBy
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        nodes {
          id
          startedAt
          endedAt
          active
          probationType
          university {
            id
            name
          }
          person {
            id
            firstName
            lastName
            displayName
            pgaId
            email
            profilePhoto
          }
          notes {
            ...CRMNoteFields
          }
        }
      }
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export type GetActiveUniversityProbationsQuery = {
  university: {
    id: string;
    probations: {
      pageInfo: {
        hasNextPage: boolean;
        endCursor?: string | null;
      };
      nodes: Array<{
        id: string;
        startedAt: string;
        active: boolean;
        probationType?: UniversityProbationType | null;
        person: { id: string };
      }>;
    };
  } | null;
};

export type GetActiveUniversityProbationsQueryVariables = {
  universityId: string;
  first?: number;
  after?: string;
};

/** Lean connection query for current-students probation badges (no notes/person details). */
export const GET_ACTIVE_UNIVERSITY_PROBATIONS_QUERY: TypedDocumentNode<
  GetActiveUniversityProbationsQuery,
  GetActiveUniversityProbationsQueryVariables
> = gql`
  query GetActiveUniversityProbations($universityId: ID!, $first: Int, $after: String) {
    university(id: $universityId) {
      id
      probations(first: $first, after: $after, activeOnly: true) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          startedAt
          active
          probationType
          person {
            id
          }
        }
      }
    }
  }
`;

export const STUDENTS_QUERY = gql`
  query getUniversityStudentsComprehensive(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeUniversityStudentsInput
  ) {
    universityStudents(first: $first, last: $last, before: $before, after: $after, input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        pgaId
        displayName
        profilePhoto
      }
    }
  }
`;
